import { ModuleTypePageTitle } from '@/enums/axiom-report.enum'
import { GFPADashboardPage } from '@/pages/GlobalFactorPerformanceAttribution/DashboardPage/GFPADashboardPage'
import { GFPADetailsPage } from '@/pages/GlobalFactorPerformanceAttribution/DetailsPage/GFPADetailsPage'
import { GFRDashboardPage } from '@/pages/GlobalFactorRiskAttribution/DashboardPage/GFRDashboardPage'
import { GFRDetailsPage } from '@/pages/GlobalFactorRiskAttribution/DetailsPage/GFRDetailsPage'
import { GFRSummaryPage } from '@/pages/GlobalFactorRiskAttribution/SummaryPage/GFRSummaryPage'
import { PortfolioOptimizationPage } from '@/pages/PortfolioOptimization/PortfolioOptimizationPage'
import PortfolioOptimizationRunReportsPage from '@/pages/PortfolioOptimizationRunReports/PortfolioOptimizationRunReportsPage'
import ManageDashboardsPage from '@/pages/RADashboard/ManageDashboardsPage'
import RADashboardPage from '@/pages/RADashboard/RADashboardPage'
import ReportSchedulesPage from '@/pages/RADashboard/ReportSchedulesPage'
import { RVDashboardPage } from '@/pages/ReturnVariance/DashboardPage/RVDashboardPage'
import { RVDetailsPage } from '@/pages/ReturnVariance/DetailsPage/RVDetailsPage'
import { ScenarioAnalysisDashboardPage } from '@/pages/ScenarioAnalysisReport/ScenarioAnalysisDashboardPage/ScenarioAnalysisDashboardPage'
import { ScenarioAnalysisSecurityDetailsPage } from '@/pages/ScenarioAnalysisReport/ScenarioAnalysisSecurityDetailsPage/ScenarioAnalysisSecurityDetailsPage'
import { ScenarioBuilderPage } from '@/pages/ScenarioBuilder/ScenarioBuilderPage'
import WhatIfReportsPage from '@/pages/WhatIfReports/WhatIfReportsPage'
import WhatIfSummaryDashboard from '@/pages/WhatIfReports/WhatIfSummaryDashboard'
import { LicenseManager } from '@ag-grid-enterprise/core'
import { GFPASummaryPage } from '@pages/GlobalFactorPerformanceAttribution/SummaryPage/GFPASummaryPage'
import { RVSummaryPage } from '@pages/ReturnVariance/SummaryPage/RVSummaryPage'
import StaticReportsPage from '@pages/StaticReportsPage/StaticReportsPage'
import React, { useEffect, useState } from 'react'
import { Route, Routes } from 'react-router-dom'
import WebFont from 'webfontloader'
import { PageTitle } from '../Base/PageTitle/PageTitle'
import { SystemInfoDialog } from '../Shared/SystemInfoDialog'
import { DefaultServiceRedirect } from './DefaultServiceRedirect'

// Ag-grid License
export const aggridAxiomLicense = `Using_this_{AG_Grid}_Enterprise_key_{AG-067505}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Clearwater_Analytics,_LLC}_is_granted_a_{Multiple_Applications}_Developer_License_for_{25}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{2}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{25_October_2024}____[v3]_[01]_MTcyOTgxMDgwMDAwMA==41b93f40cffda911e3d65e3955274e01`
LicenseManager.setLicenseKey(aggridAxiomLicense)

const App: React.FunctionComponent = () => {
  // Loading WebFont is necessary since we are using a variable font "Source Sans 3" with open source license from google fonts.
  useEffect(() => {
    WebFont.load({
      google: {
        families: ['Source Sans 3:200,300,400,600,700,900'],
      },
    })
  }, [])

  const [systemInfoOpen, setSystemInfoOpen] = useState(false)

  // Ctrl+Alt+S opens a System Info dialog (realm, environment, API URLs, etc.) for support/debugging.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.altKey && event.key.toLowerCase() === 's') {
        event.preventDefault()
        setSystemInfoOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <>
      <Routes>
        {/* Landing Page | Static Reports */}
        <Route
          path='/reports'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <StaticReportsPage />
            </>
          }
        />

        {/* Report Variance */}
        <Route
          path='/reports/:reportId/rv/summary'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <RVSummaryPage />
            </>
          }
        />
        <Route
          path='/reports/:reportId/rv/dashboard'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <RVDashboardPage />
            </>
          }
        />
        <Route
          path='/reports/:reportId/rv/details'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <RVDetailsPage />
            </>
          }
        />

        {/* Global Factor Performance Attribution Report */}
        <Route
          path='/reports/:reportId/gfpa/dashboard'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <GFPADashboardPage />
            </>
          }
        />
        <Route
          path='/reports/:reportId/gfpa/daily-attribution'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <GFPASummaryPage />
            </>
          }
        />
        <Route
          path='/reports/:reportId/gfpa/detail'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <GFPADetailsPage />
            </>
          }
        />

        {/* Global Factor Risk Report */}
        <Route
          path='/reports/:reportId/gfr/dashboard'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <GFRDashboardPage />
            </>
          }
        />
        <Route
          path='/reports/:reportId/gfr/summary'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <GFRSummaryPage />
            </>
          }
        />
        <Route
          path='/reports/:reportId/gfr/detail'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <GFRDetailsPage />
            </>
          }
        />

        {/* Scenario Analysis Report */}
        <Route
          path='/reports/:reportId/scenario-analysis/dashboard'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <ScenarioAnalysisDashboardPage />
            </>
          }
        />
        <Route
          path='/reports/:reportId/scenario-analysis/security-details'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.REPORTING} />
              <ScenarioAnalysisSecurityDetailsPage />
            </>
          }
        />

        <Route
          path='/scenario-builder'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.SCENARIO_BUILDER} />
              <ScenarioBuilderPage title={ModuleTypePageTitle.SCENARIO_BUILDER} />
            </>
          }
        />

        <Route
          path='/portfolio-optimization'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.PORTFOLIO_OPTIMIZATION} />
              <PortfolioOptimizationPage />
            </>
          }
        />

        <Route
          path='/optimization-reports/:reportId/optim/reports'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.PORTFOLIO_OPTIMIZATION} />
              <PortfolioOptimizationRunReportsPage />
            </>
          }
        />

        <Route
          path='/what-if-analysis'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.WHAT_IF_REPORTS} />
              <WhatIfReportsPage title='Clearwater Wilshire Axiom' />
            </>
          }
        />

        <Route
          path='/what-if-analysis/dashboard'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.WHAT_IF_REPORTS} />
              <WhatIfSummaryDashboard title='Clearwater Wilshire Axiom' />
            </>
          }
        />

        {/* Factor Analytics dashboard */}
        <Route
          path='/factor-analytics'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.FACTOR_ANALYTICS} />
              <RADashboardPage />
            </>
          }
        />

        <Route
          path='/factor-analytics/manage-dashboards'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.FACTOR_ANALYTICS} />
              <ManageDashboardsPage />
            </>
          }
        />

        <Route
          path='/factor-analytics/report-schedules'
          element={
            <>
              <PageTitle title={ModuleTypePageTitle.FACTOR_ANALYTICS} />
              <ReportSchedulesPage />
            </>
          }
        />

        {/* Catch All: redirect to the user's recommended default service */}
        <Route path='*' element={<DefaultServiceRedirect />} />
      </Routes>
      <SystemInfoDialog open={systemInfoOpen} onClose={() => setSystemInfoOpen(false)} />
    </>
  )
}

export default App
